import { useState, useEffect } from "react";

interface VirtualKeyboardState {
  isKeyboardOpen: boolean;
  keyboardHeight: number;
  /** How far iOS has shifted the visual viewport down from the top (px). */
  offsetTop: number;
  /** The actual visible viewport height (accounts for keyboard). */
  viewportHeight: number;
}

export function useVirtualKeyboard({ enabled = true }: { enabled?: boolean } = {}): VirtualKeyboardState {
  const [state, setState] = useState<VirtualKeyboardState>({
    isKeyboardOpen: false,
    keyboardHeight: 0,
    offsetTop: 0,
    viewportHeight: window.visualViewport?.height ?? window.innerHeight,
  });

  useEffect(() => {
    if (!enabled) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    let wasKeyboardOpen = false;
    let resetTimers: ReturnType<typeof setTimeout>[] = [];

    // Force WebKit to recalculate viewport position after keyboard close.
    // A tiny scrollBy nudge is more reliable than scrollTo(0,0) because it
    // triggers a reflow without fighting the browser's own scroll state.
    const forceViewportReset = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      // The scrollBy nudge trick — forces WKWebView to recalculate layout
      window.scrollBy(0, -1);
      window.scrollBy(0, 1);
    };

    // Also listen for focusout — the most reliable signal that the keyboard
    // is about to dismiss on iOS Chrome (WKWebView).
    const handleFocusOut = () => {
      // Small delay lets the keyboard animation begin before we reset
      const t = setTimeout(forceViewportReset, 100);
      resetTimers.push(t);
    };

    const update = () => {
      const keyboardHeight = window.innerHeight - viewport.height;
      const nextIsKeyboardOpen = keyboardHeight > 100;
      const nextKeyboardHeight = Math.max(0, keyboardHeight);
      const nextOffsetTop = viewport.offsetTop;
      const nextViewportHeight = viewport.height;

      if (wasKeyboardOpen && !nextIsKeyboardOpen) {
        // Clear any pending resets
        resetTimers.forEach(t => clearTimeout(t));
        resetTimers = [];
        // Immediate + staggered resets to catch iOS animation timing
        forceViewportReset();
        resetTimers.push(setTimeout(forceViewportReset, 50));
        resetTimers.push(setTimeout(forceViewportReset, 150));
        resetTimers.push(setTimeout(forceViewportReset, 300));
      }
      wasKeyboardOpen = nextIsKeyboardOpen;

      setState((prev) => {
        if (
          prev.isKeyboardOpen === nextIsKeyboardOpen &&
          prev.keyboardHeight === nextKeyboardHeight &&
          prev.offsetTop === nextOffsetTop &&
          prev.viewportHeight === nextViewportHeight
        ) {
          return prev;
        }
        return {
          isKeyboardOpen: nextIsKeyboardOpen,
          keyboardHeight: nextKeyboardHeight,
          offsetTop: nextOffsetTop,
          viewportHeight: nextViewportHeight,
        };
      });
    };

    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    window.addEventListener("focusout", handleFocusOut);
    update();
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      window.removeEventListener("focusout", handleFocusOut);
      resetTimers.forEach(t => clearTimeout(t));
    };
  }, [enabled]);

  return state;
}
